/* Patch 15 — Financial Truth Reconciliation Center
   ====================================================
   Full audit-grade view of all 6 reconciliation checks for a period.
   Read-only. Shows expected vs actual, variance, and detailed context
   when a check fails. Surfaces anomalies the CFO must inspect *before*
   closing the books. */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, CheckCircle2, AlertTriangle, XCircle,
  ShieldCheck, ShieldAlert, ChevronRight, ChevronDown, Search, Info, Settings2,
} from 'lucide-react';
import InvestigationDrawer, { CHECK_NAME_TO_KEY } from '../components/InvestigationDrawer';
import SystemAssumptionsPanel from '../components/reconciliation/SystemAssumptionsPanel';
import HowItWorksPanel from '../components/reconciliation/HowItWorksPanel';
import IssueCard from '../components/edre/IssueCard';
import { Button } from '../components/ui/button';

const fmt = (n) => {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
};

const today = new Date();
const defaultPeriod = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

const STATUS_META = {
  PASS:            { cls: 'bg-emerald-50 border-emerald-300 text-emerald-800', Icon: CheckCircle2,  badge: 'bg-emerald-600' },
  MATCH:           { cls: 'bg-emerald-50 border-emerald-300 text-emerald-800', Icon: CheckCircle2,  badge: 'bg-emerald-600' },
  WARNING:         { cls: 'bg-amber-50   border-amber-300   text-amber-900',   Icon: AlertTriangle, badge: 'bg-amber-500'   },
  FAIL:            { cls: 'bg-rose-50    border-rose-300    text-rose-900',    Icon: XCircle,       badge: 'bg-rose-600'    },
  NOT_DUE_YET:     { cls: 'bg-sky-50     border-sky-300     text-sky-900',     Icon: Info,          badge: 'bg-sky-500'     },
  NOT_CONFIGURED:  { cls: 'bg-slate-50   border-slate-300   text-slate-800',   Icon: Settings2,     badge: 'bg-slate-500'   },
  ACTION_NEEDED:   { cls: 'bg-indigo-50  border-indigo-300  text-indigo-900',  Icon: Info,          badge: 'bg-indigo-500'  },
};

// Variance classification chip metadata (STA-6).
const VC_META = {
  none:              { cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: '🟢' },
  timing:            { cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: '🟢' },
  config_missing:    { cls: 'bg-amber-100   text-amber-800   border-amber-200',   icon: '🟡' },
  missing_posting:   { cls: 'bg-orange-100  text-orange-800  border-orange-200',  icon: '🟠' },
  data_integrity:    { cls: 'bg-rose-100    text-rose-800    border-rose-200',    icon: '🔴' },
  manual_adjustment: { cls: 'bg-sky-100     text-sky-800     border-sky-200',     icon: '🔵' },
  unknown:           { cls: 'bg-slate-200   text-slate-700   border-slate-300',   icon: '⚫' },
};

function VarianceClassChip({ vc }) {
  if (!vc) return null;
  const meta = VC_META[vc.code] || VC_META.unknown;
  return (
    <span className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.cls}`}
          data-testid={`variance-class-${vc.code}`}
          title={vc.en}>
      <span>{meta.icon}</span> {vc.ar}
    </span>
  );
}

function ConfidenceBadge({ score, reasons }) {
  if (score === undefined || score === null) return null;
  const cls = score >= 90 ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
              : score >= 70 ? 'bg-amber-100 text-amber-800 border-amber-200'
              : 'bg-rose-100 text-rose-800 border-rose-200';
  return (
    <span
      className={`inline-flex items-center border rounded-full px-2 py-0.5 text-[10px] font-mono font-bold ${cls}`}
      data-testid={`confidence-badge-${score}`}
      title={(reasons || []).join(' • ') || `Confidence: ${score}%`}
    >
      {score}% موثوقية
    </span>
  );
}

const VERDICT_META = {
  CLEAN:       { cls: 'bg-emerald-50 border-emerald-300', label: 'CLEAN — All reconciliations passed', text: 'text-emerald-800' },
  WARNING:     { cls: 'bg-amber-50   border-amber-300',   label: 'WARNING — Minor variances detected', text: 'text-amber-900'   },
  INVESTIGATE: { cls: 'bg-rose-50    border-rose-300',    label: 'INVESTIGATE — Material variance — review before close', text: 'text-rose-900' },
  UNKNOWN:     { cls: 'bg-slate-50   border-slate-300',   label: 'UNKNOWN', text: 'text-slate-700' },
};

const BUCKET_META = {
  aligned:         { label: 'Aligned (matches Return)',                 hint: 'Normal flow — counted by both VAT Return and GL.' },
  reversed_only:   { label: 'Reversed journals (GL only)',              hint: 'status=reversed → excluded by VAT Return but still on GL.' },
  code_mismatch:   { label: 'Code mismatch (GL only)',                  hint: 'account_number is a VAT account but account_code is NOT VAT_INPUT/VAT_OUTPUT.' },
  number_mismatch: { label: 'Number mismatch (Return only)',            hint: 'account_code is VAT_INPUT/VAT_OUTPUT but account_number is NOT 11300/21300/21310.' },
};

function VATDesyncDrillDown({ returnId, hostVariance }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const [wideningDays, setWideningDays] = useState(0);
  const [postingPreview, setPostingPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  if (!returnId) return null;

  const runDiagnose = async (widen_days = 0) => {
    setLoading(true);
    try {
      const url = widen_days > 0
        ? `/vat/returns/${returnId}/desync/diagnose?widen_days=${widen_days}`
        : `/vat/returns/${returnId}/desync/diagnose`;
      const r = await axiosInstance.get(url);
      setData(r.data);
      setWideningDays(widen_days);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Diagnose failed');
    } finally { setLoading(false); }
  };

  const runWiden = async () => {
    // Widen by 90 days on each side — reveals late-posted journals
    // whose ``journal_date`` fell outside the return window.
    await runDiagnose(90);
    toast.success('نافذة التشخيص وُسِّعت إلى ±90 يوماً');
  };

  const runPostingPreview = async () => {
    setPreviewLoading(true);
    try {
      const r = await axiosInstance.get(
        `/vat/returns/${returnId}/preview-by-posting-date`);
      setPostingPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setPreviewLoading(false); }
  };

  const runBackfill = async () => {
    if (!window.confirm(
      `Post corrective VAT_INPUT lines to ${data?.orphan_count || 0} bill journal(s)?`))
      return;
    setRepairing(true);
    try {
      const r = await axiosInstance.post(
        `/vat/returns/${returnId}/desync/backfill`);
      const rep = r.data.repaired || [];
      const skp = r.data.skipped  || [];
      const ac  = r.data.auto_closed;
      toast.success(
        `Repaired ${rep.length} journal(s) · added ${fmt(r.data.total_added)} SAR to GL`
        + (skp.length ? ` · skipped ${skp.length}` : '')
        + (ac         ? ` · Auto-close ${fmt(ac.amount)} SAR (${ac.zone})` : ''));
      if (ac) {
        toast.info(`✨ Auto-close · ${ac.journal_no} · ${ac.note}`,
                    { duration: 8000 });
      }
      setData(r.data.diagnostic);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally { setRepairing(false); }
  };

  const jow = data?.journals_outside_window || [];
  const jowVatSum = jow.reduce((s, r) => s + (Number(r?.vat_sum) || 0), 0);
  const hasHostVariance = typeof hostVariance === 'number'
                          && Math.abs(hostVariance) > 0.5;

  return (
    <div className="mt-3 border border-rose-200 bg-rose-50 rounded p-3"
         data-testid="vat-desync-drilldown">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-rose-800">
          🔍 Deep Diagnose · Bills counted by Return but missing in GL
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={runDiagnose}
                  disabled={loading} data-testid="btn-desync-diagnose">
            {loading ? '…' : (data ? 'Refresh' : 'Diagnose')}
          </Button>
          {data?.orphan_count > 0 && (
            <Button size="sm" variant="destructive" onClick={runBackfill}
                    disabled={repairing} data-testid="btn-desync-backfill">
              {repairing ? '…' : `Backfill ${data.orphan_count} Journal(s)`}
            </Button>
          )}
        </div>
      </div>
      <p className="text-[11px] text-slate-600 mt-1">
        When all 4 attribution buckets net to zero, the gap comes from
        bills whose <code>vat_amount</code> was captured on the sub-ledger
        but their GL journal has no VAT_INPUT line. This scan finds those
        bills. <b>Backfill</b> is admin-only and appends corrective
        journal lines without altering the original bill.
      </p>
      {data && (
        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div className="bg-white/70 rounded p-2 border border-rose-100">
            <div className="text-[10px] uppercase text-slate-500">Bills VAT total</div>
            <div className="font-mono font-bold text-sm">{fmt(data.total_bill_vat)}</div>
          </div>
          <div className="bg-white/70 rounded p-2 border border-rose-100">
            <div className="text-[10px] uppercase text-slate-500">GL VAT total</div>
            <div className="font-mono font-bold text-sm">{fmt(data.total_journal_vat)}</div>
          </div>
          <div className="bg-white/70 rounded p-2 border border-rose-100">
            <div className="text-[10px] uppercase text-slate-500">Unattributed gap</div>
            <div className="font-mono font-bold text-sm text-rose-700">{fmt(data.unattributed_gap)}</div>
          </div>
          <div className="bg-white/70 rounded p-2 border border-rose-100">
            <div className="text-[10px] uppercase text-slate-500">Orphan bills</div>
            <div className="font-mono font-bold text-sm text-rose-700">{data.orphan_count}</div>
          </div>
        </div>
      )}
      {/* Feb 2026 · Forensic actions — visible whenever a snapshot was
          located (data present). They stay clickable even after the
          Backfill has closed all orphans, because a residual variance
          usually means a date-positioning issue that widening the
          window (or re-filing on posting-date) will surface. */}
      {data && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="vat-desync-forensic-actions">
          <Button size="sm" variant="outline"
                  onClick={runWiden} disabled={loading}
                  data-testid="btn-widen-drill-window"
                  title="Re-scan the return window ± 90 days to reveal journals posted late/early">
            🕰️ Widen drill window (±90 d)
          </Button>
          <Button size="sm" variant="outline"
                  onClick={runPostingPreview} disabled={previewLoading}
                  data-testid="btn-refile-on-posting-date"
                  title="Preview what the return would look like if bucketed by GL posting_date instead of bill_date">
            📅 Re-file on posting-date (preview)
          </Button>
          {wideningDays > 0 && (
            <span className="inline-flex items-center px-2 py-1 rounded bg-indigo-50 border border-indigo-200 text-[11px] text-indigo-700 font-semibold"
                  data-testid="widen-indicator">
              نافذة موسَّعة: ±{wideningDays} يوم · {data?.effective_start_date} → {data?.effective_end_date}
            </span>
          )}
        </div>
      )}
      {/* Date-positioning telemetry — makes the "371.62" style gaps
          concrete. Each row shows a journal whose date sits OUTSIDE
          the return window; the sum here PLUS unattributed_gap fully
          accounts for the parent reconciliation variance. */}
      {jow.length > 0 && (
        <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2"
             data-testid="journals-outside-window">
          <div className="flex items-center justify-between flex-wrap gap-1">
            <div className="text-[11px] font-bold uppercase tracking-wider text-amber-900">
              🕰️ {jow.length} قيد بتاريخ posting خارج نافذة الإقرار · Σ VAT: <span className="font-mono">{fmt(jowVatSum)}</span>
            </div>
          </div>
          <p className="text-[11px] text-amber-800 mt-1">
            هذه القيود تحمل مبلغ VAT_INPUT الصحيح ومربوطة بفواتير ضمن الإقرار
            (عبر <code>bill_date</code> / <code>expense_date</code>). محرك الـ
            Reconciliation الآن يحسب رصيد GL على أساس **تاريخ الوثيقة المصدر**
            (ZATCA-compliant)، لذا هذه القيود مُحتسَبة بشكل صحيح في المقارنة
            ولا تُنتج فرقاً — الجدول هنا للأثر التدقيقي فقط.
          </p>
          <div className="mt-1.5 max-h-40 overflow-y-auto bg-white rounded border border-amber-100">
            <table className="w-full text-[11px]">
              <thead className="bg-amber-100 text-amber-900 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1">Journal #</th>
                  <th className="text-left px-2 py-1">Date</th>
                  <th className="text-left px-2 py-1">Direction</th>
                  <th className="text-right px-2 py-1">Entries</th>
                  <th className="text-right px-2 py-1">Σ VAT</th>
                </tr>
              </thead>
              <tbody>
                {jow.map((r, i) => (
                  <tr key={i} className="border-t border-amber-100">
                    <td className="px-2 py-1 font-mono">{r.journal_no}</td>
                    <td className="px-2 py-1 font-mono">{r.journal_date}</td>
                    <td className="px-2 py-1 text-[10px]">
                      {r.direction === 'after_window' ? 'بعد النافذة' : 'قبل النافذة'}
                    </td>
                    <td className="px-2 py-1 font-mono text-right">{r.entries_count}</td>
                    <td className="px-2 py-1 font-mono text-right">{fmt(r.vat_sum)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Re-file preview panel */}
      {postingPreview && (
        <div className="mt-2 bg-sky-50 border border-sky-200 rounded p-2"
             data-testid="posting-date-preview">
          <div className="text-[11px] font-bold uppercase tracking-wider text-sky-900 mb-1">
            📅 What the return would look like on POSTING date
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
            <div className="bg-white rounded p-2 border border-sky-100">
              <div className="text-[10px] uppercase text-slate-500">Filed (bill_date)</div>
              <div className="font-mono">Input: <b>{fmt(postingPreview.filed?.input_vat)}</b></div>
              <div className="font-mono">Output: <b>{fmt(postingPreview.filed?.output_vat)}</b></div>
              <div className="font-mono">Net: <b>{fmt(postingPreview.filed?.net)}</b></div>
            </div>
            <div className="bg-white rounded p-2 border border-sky-100">
              <div className="text-[10px] uppercase text-slate-500">Posting-date basis</div>
              <div className="font-mono">Input: <b>{fmt(postingPreview.posting_date?.input_vat)}</b></div>
              <div className="font-mono">Output: <b>{fmt(postingPreview.posting_date?.output_vat)}</b></div>
              <div className="font-mono">Net: <b>{fmt(postingPreview.posting_date?.net)}</b></div>
            </div>
            <div className="bg-white rounded p-2 border border-sky-100">
              <div className="text-[10px] uppercase text-slate-500">Delta (posting − filed)</div>
              <div className="font-mono text-sky-900">Input: <b>{fmt(postingPreview.delta?.input_vat)}</b></div>
              <div className="font-mono text-sky-900">Output: <b>{fmt(postingPreview.delta?.output_vat)}</b></div>
              <div className="font-mono text-sky-900">Net: <b>{fmt(postingPreview.delta?.net)}</b></div>
            </div>
          </div>
          <p className="text-[10px] text-sky-800 italic mt-1">{postingPreview.note}</p>
        </div>
      )}
      {data?.orphans?.length > 0 && (
        <div className="mt-2 max-h-64 overflow-y-auto bg-white rounded border border-rose-200">
          <table className="w-full text-[11px]" data-testid="desync-orphans-table">
            <thead className="bg-rose-100 text-rose-900 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1">Bill #</th>
                <th className="text-left px-2 py-1">Date</th>
                <th className="text-left px-2 py-1">Supplier</th>
                <th className="text-right px-2 py-1">Bill VAT</th>
                <th className="text-right px-2 py-1">GL VAT</th>
                <th className="text-right px-2 py-1">Δ</th>
                <th className="text-left px-2 py-1">Reason</th>
              </tr>
            </thead>
            <tbody>
              {data.orphans.map((o, i) => (
                <tr key={i} className="border-t border-rose-100">
                  <td className="px-2 py-1 font-mono">{o.bill_number || '—'}</td>
                  <td className="px-2 py-1 font-mono">{o.bill_date || '—'}</td>
                  <td className="px-2 py-1">{o.supplier_name || '—'}</td>
                  <td className="px-2 py-1 font-mono text-right">{fmt(o.bill_vat_amount)}</td>
                  <td className="px-2 py-1 font-mono text-right">{fmt(o.gl_vat_amount)}</td>
                  <td className="px-2 py-1 font-mono text-right text-rose-700 font-bold">{fmt(o.delta)}</td>
                  <td className="px-2 py-1 text-slate-600 text-[10px]">{o.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.orphan_count === 0 && !hasHostVariance && jow.length === 0 && (
        <p className="text-xs text-emerald-700 mt-2 font-semibold">
          ✓ No orphan bills — every filed VAT amount has a matching GL line.
        </p>
      )}
      {data && data.orphan_count === 0 && (hasHostVariance || jow.length > 0) && (
        <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 mt-2"
             data-testid="date-positioning-hint">
          ✓ Sub-ledger reconciles fully (0 orphans). محرك الـ Reconciliation
          يحسب رصيد GL الآن على أساس **تاريخ الوثيقة المصدر**
          (bill_date / expense_date / invoice_date) — نفس القاعدة التي يستخدمها
          ZATCA. لذا القيود التي posting_date فيها خارج النافذة تُحتسَب
          بشكل صحيح ولا تنتج فرقاً في الإقرار. الجدول أعلاه للاطلاع فقط.
        </div>
      )}
    </div>
  );
}

function GhostVATLinesPanel({ returnId, hostVariance }) {
  const [data, setData] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [healBusy, setHealBusy] = React.useState(false);
  const need = Math.abs(hostVariance || 0) > 0.5;

  const fetch = React.useCallback(async () => {
    if (!returnId) return;
    setLoading(true);
    try {
      const { data: r } = await axiosInstance.get(
        `/vat/returns/${returnId}/ghost-vat-lines`);
      setData(r);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [returnId]);

  React.useEffect(() => {
    if (need && !data) fetch();
  }, [need, data, fetch]);

  const heal = async (action) => {
    if (!data?.ghost_lines?.length) return;
    const journalIds = [...new Set(
      data.ghost_lines.map(l => l.journal_id).filter(Boolean))];
    if (!journalIds.length) return;
    const label = action === 'acknowledge'
      ? `اعتماد ${journalIds.length} قيد كتعديل يدوي (بلا تغيير على GL)؟`
      : `عكس ${journalIds.length} قيد شبَحي (post reversal journals)؟`;
    if (!window.confirm(label)) return;
    setHealBusy(true);
    try {
      const { data: res } = await axiosInstance.post(
        `/vat/returns/${returnId}/ghost-vat-lines/heal`,
        { action, journal_ids: journalIds });
      const c = res.counts || {};
      toast.success(`تم: ${c.done || 0} · أخطاء: ${c.errors || 0}`);
      fetch();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(typeof msg === 'string' ? msg : 'Heal failed');
    } finally {
      setHealBusy(false);
    }
  };

  if (!need) return null;
  const gap = Math.abs(hostVariance || 0);
  const netEffect = Math.abs(data?.total_net_effect || 0);
  const explainsIt = data && netEffect > 0 && Math.abs(gap - netEffect) <= Math.max(1, 0.05 * gap);

  return (
    <div className="mt-3 rounded border border-fuchsia-300 bg-fuchsia-50 p-3"
         data-testid="ghost-vat-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-bold text-fuchsia-900 uppercase tracking-wider">
          👻 قيود VAT شبَحية — لا مَصدَر لَها (ghost lines)
        </div>
        <div className="flex items-center gap-2">
          {(data?.ghost_lines || []).length > 0 && (
            <>
              <button
                onClick={() => heal('acknowledge')}
                disabled={healBusy}
                data-testid="ghost-ack-btn"
                className="text-[10px] font-semibold px-2 py-1 rounded border border-fuchsia-300 text-fuchsia-800 hover:bg-fuchsia-100 disabled:opacity-50"
                title="اعتماد كتعديل يدوي (بلا تغيير GL) — الفرق سيُفسَّر"
              >
                ✓ اعتمد كتعديل يدوي
              </button>
              <button
                onClick={() => heal('reverse')}
                disabled={healBusy}
                data-testid="ghost-reverse-btn"
                className="text-[10px] font-semibold px-2 py-1 rounded border border-rose-300 text-rose-800 hover:bg-rose-50 disabled:opacity-50"
                title="عكس (post reversal journals) — يُصفِّر الأثر من GL"
              >
                ↺ عكس الكل
              </button>
            </>
          )}
          <button onClick={() => setOpen(!open)}
                  data-testid="ghost-vat-toggle"
                  className="text-[10px] font-semibold text-fuchsia-800 hover:underline">
            {open ? 'إخفاء' : 'عرض الأسطر'}
          </button>
        </div>
      </div>
      {loading && <div className="text-[11px] text-fuchsia-700">جاري التحميل...</div>}
      {data && data.error && (
        <div className="text-[11px] text-rose-700">خطأ: {data.error}</div>
      )}
      {data && !data.error && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px] font-mono mb-2">
            <div>
              <div className="text-fuchsia-700 text-[9px] uppercase">Ghost Lines</div>
              <div className="font-bold" data-testid="ghost-line-count">{data.ghost_line_count}</div>
            </div>
            <div>
              <div className="text-fuchsia-700 text-[9px] uppercase">Ghost Journals</div>
              <div className="font-bold">{data.ghost_journal_count}</div>
            </div>
            <div>
              <div className="text-fuchsia-700 text-[9px] uppercase">Input Net (Dr − Cr)</div>
              <div className="font-bold">{fmt(data.total_input_net)}</div>
            </div>
            <div>
              <div className="text-fuchsia-700 text-[9px] uppercase">Output Net (Cr − Dr)</div>
              <div className="font-bold">{fmt(data.total_output_net)}</div>
            </div>
          </div>
          <div className="text-[11px] mb-2 rounded p-2 border"
                style={{
                  background: explainsIt ? '#dcfce7' : '#fef3c7',
                  borderColor: explainsIt ? '#86efac' : '#fcd34d',
                }}
                data-testid="ghost-vat-verdict">
            {explainsIt ? (
              <span className="text-emerald-900">
                ✓ صافي أثَر القُيود الشَّبَحية <b className="font-mono">{fmt(data.total_net_effect)}</b> SAR
                يُفَسِّر الفَرق المُتَبَقّي <b className="font-mono">{fmt(hostVariance)}</b> SAR بِالكامِل.
                هذه القُيود هي المَصدَر — راجِعها وقَرِّر إن كانَ يَجِب حَذفُها أو الإبقاء عليها.
              </span>
            ) : (
              <span className="text-amber-900">
                القُيود الشَّبَحية تُساهِم بـ <b className="font-mono">{fmt(data.total_net_effect)}</b> SAR
                — الفَرق المُتَبَقّي <b className="font-mono">{fmt(hostVariance)}</b> SAR،
                لذا يوجَد مَصدَر إضافي آخَر (رُبَّما فَواتير بِلا سَطر VAT_INPUT).
              </span>
            )}
          </div>
          <div className="text-[10px] text-fuchsia-800 mb-2">{data.help_ar}</div>
          {open && (data.ghost_lines || []).length > 0 && (
            <div className="max-h-60 overflow-auto border border-fuchsia-200 rounded">
              <table className="w-full text-[10px]" data-testid="ghost-lines-table">
                <thead className="bg-fuchsia-100 text-fuchsia-900 sticky top-0">
                  <tr>
                    <th className="text-left px-1.5 py-1">Journal #</th>
                    <th className="text-left px-1.5 py-1">Date</th>
                    <th className="text-left px-1.5 py-1">Account</th>
                    <th className="text-left px-1.5 py-1">Code</th>
                    <th className="text-right px-1.5 py-1">Dr</th>
                    <th className="text-right px-1.5 py-1">Cr</th>
                    <th className="text-right px-1.5 py-1">Net Effect</th>
                    <th className="text-left px-1.5 py-1">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.ghost_lines || []).map((ln, i) => (
                    <tr key={i}
                        className="border-t border-fuchsia-100 hover:bg-fuchsia-50"
                        data-testid={`ghost-line-${i}`}>
                      <td className="px-1.5 py-1 font-mono">
                        <Link to={`/journal?focus=${ln.journal_id}`}
                              className="text-indigo-700 underline">
                          {ln.journal_no || '—'}
                        </Link>
                      </td>
                      <td className="px-1.5 py-1 font-mono">{ln.journal_date}</td>
                      <td className="px-1.5 py-1 font-mono">{ln.account_number}</td>
                      <td className="px-1.5 py-1 font-mono">{ln.account_code || '—'}</td>
                      <td className="px-1.5 py-1 font-mono text-right">{fmt(ln.debit)}</td>
                      <td className="px-1.5 py-1 font-mono text-right">{fmt(ln.credit)}</td>
                      <td className="px-1.5 py-1 font-mono text-right font-bold">
                        {fmt(ln.net_effect)} ({ln.side === 'output' ? 'Out' : 'In'})
                      </td>
                      <td className="px-1.5 py-1 text-slate-700 max-w-xs truncate"
                          title={ln.description}>
                        {ln.description || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}


function VATDrillDown({ drill, returnId, hostVariance, hostDetails }) {
  if (!drill) return null;
  const nonZero = Object.entries(drill).filter(([k, v]) => k !== 'aligned' && Math.abs(v?.net || 0) > 0.5);
  const explained = Boolean(hostDetails?.explained_by_late_posts);
  const lateSum   = Number(hostDetails?.late_journal_vat_sum || 0);
  const lateCount = Number(hostDetails?.late_journal_count || 0);
  const subTotal  = Number(hostDetails?.subledger_total || 0);
  return (
    <div className="mt-3 bg-white/70 rounded border border-amber-200 p-3" data-testid="vat-drill-down">
      <div className="flex items-center gap-1.5 font-bold text-amber-900 text-xs uppercase tracking-wider mb-2">
        <Search size={12} /> VAT variance attribution
      </div>
      {explained && (
        <div className="mb-2 p-2 rounded border border-emerald-300 bg-emerald-50 text-[11px] text-emerald-900"
             data-testid="explained-variance-banner">
          ✓ <b>Variance explained · لا حاجة لأي إجراء</b> — الفرق البالغ {fmt(hostVariance)} SAR
          بين &quot;Actual (GL)&quot; و &quot;Expected (Return)&quot; ناتج عن قيود
          بتاريخ posting خارج نافذة الإقرار (عددها{' '}
          <b>{lateCount}</b> بمجموع{' '}
          <span className="font-mono">{fmt(lateSum)}</span> SAR).
          الـ Sub-Ledger مطابق تماماً عند{' '}
          <span className="font-mono">{fmt(subTotal)}</span> SAR ✓،
          والمقارنة الآن تعتمد على تاريخ الوثيقة المصدر (ZATCA-compliant)
          فلا تأثير على الإقرار.
        </div>
      )}
      <p className="text-[11px] text-slate-600 mb-2">
        كل بكِت يعرض الآن رقمَين: الأزرق حسب <b>تاريخ القيد (journal_date)</b>
        — للأثر التدقيقي، والأخضر ⭐ حسب <b>تاريخ الوثيقة المصدر</b>
        (bill_date / expense_date / invoice_date) — وهو المعتمد لدى ZATCA
        والمطابق لقيمة الإقرار.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {Object.entries(drill).map(([key, b]) => {
          const meta = BUCKET_META[key] || { label: key, hint: '' };
          const net = Number(b?.net || 0);
          const cls = key === 'aligned'
            ? 'bg-emerald-50 border-emerald-200'
            : Math.abs(net) > 0.5
              ? 'bg-rose-50 border-rose-200'
              : 'bg-slate-50 border-slate-200';
          return (
            <div key={key} className={`rounded border p-2 ${cls}`} data-testid={`vat-bucket-${key}`}>
              <div className="text-[11px] font-bold uppercase tracking-wider opacity-80">{meta.label}</div>
              <div className="font-mono text-base font-bold mt-1">
                Net {fmt(net)} <span className="text-[10px] opacity-60">(out {fmt(b?.out)} − in {fmt(b?.inp)})</span>
                <span className="text-[9px] font-normal text-slate-500 ml-1">
                  · by journal_date
                </span>
              </div>
              {/* Feb 2026 · ZATCA source-date view — same bucket
                  aggregated by bill_date/expense_date/invoice_date */}
              {b?.source_net !== undefined && Math.abs(b.source_net || 0) > 0.005 && (
                <div className="font-mono text-sm font-bold mt-0.5 text-emerald-800"
                     data-testid={`vat-bucket-${key}-src`}>
                  Net {fmt(b.source_net)}
                  <span className="text-[10px] opacity-60 ml-1">
                    (out {fmt(b?.src_out)} − in {fmt(b?.src_inp)})
                  </span>
                  <span className="text-[9px] font-normal text-emerald-600 ml-1">
                    ⭐ by source-date (ZATCA · matches Return)
                  </span>
                </div>
              )}
              <p className="text-[10px] text-slate-600 mt-1">{meta.hint}</p>
              {key === 'aligned' && explained && (
                <p className="text-[10px] text-emerald-800 mt-1 font-semibold"
                   data-testid="aligned-bucket-note">
                  ⓘ الرقم الأزرق أعلاه (Net {fmt(b?.net)}) يشمل فقط قيود
                  <code className="mx-0.5 bg-white px-1 rounded">journal_date</code>
                  داخل نافذة الإقرار. الرقم الأخضر أدناه (source-date)
                  هو الذي تعتمده ZATCA — وهو مطابق لقيمة الإقرار.
                </p>
              )}
              {Array.isArray(b?.lines) && b.lines.length > 0 && (
                <details className="mt-1.5">
                  <summary className="cursor-pointer text-[11px] text-indigo-700 font-semibold">
                    {b.lines.length} sample line{b.lines.length === 1 ? '' : 's'}
                  </summary>
                  <div className="mt-1.5 max-h-48 overflow-y-auto">
                    <table className="w-full text-[11px]">
                      <thead className="bg-white/60 text-slate-600">
                        <tr>
                          <th className="text-left px-1.5 py-1">Journal</th>
                          <th className="text-left px-1.5 py-1">Date</th>
                          <th className="text-left px-1.5 py-1">Acct #</th>
                          <th className="text-left px-1.5 py-1">Code</th>
                          <th className="text-right px-1.5 py-1">Dr</th>
                          <th className="text-right px-1.5 py-1">Cr</th>
                        </tr>
                      </thead>
                      <tbody>
                        {b.lines.map((ln, i) => (
                          <tr key={i} className="border-t border-white/60">
                            <td className="px-1.5 py-1 font-mono">{ln.journal_no || '—'}</td>
                            <td className="px-1.5 py-1 font-mono">{ln.journal_date || '—'}</td>
                            <td className="px-1.5 py-1 font-mono">{ln.account_number || '—'}</td>
                            <td className="px-1.5 py-1 font-mono">{ln.account_code || '—'}</td>
                            <td className="px-1.5 py-1 font-mono text-right">{fmt(ln.debit)}</td>
                            <td className="px-1.5 py-1 font-mono text-right">{fmt(ln.credit)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </div>
          );
        })}
      </div>
      {nonZero.length === 0 && (
        <>
          {Math.abs(hostVariance || 0) > 0.5 ? (
            <p className="text-[11px] text-amber-800 mt-2 bg-amber-50 border border-amber-200 rounded p-2"
               data-testid="all-buckets-zero-with-variance">
              Non-aligned buckets (reversed / code-mismatch /
              number-mismatch) net to zero, but a residual variance of{' '}
              <span className="font-mono font-bold">{fmt(hostVariance)}</span> SAR remains.
              محرك المقارنة يعتمد الآن على <b>تاريخ الوثيقة المصدر</b>
              (bill_date / expense_date / invoice_date) — نفس القاعدة
              التي تعتمدها ZATCA. الفرق المتبقي إما (1) قيود يدوية بلا
              وثيقة مصدر، أو (2) فواتير مسجَّلة في الإقرار وقيدها ينقصه
              سطر VAT_INPUT فعلياً. راجع «قيود VAT شبَحية» أدناه.
            </p>
          ) : (
            <p className="text-[11px] text-emerald-700 mt-2 font-semibold"
               data-testid="all-buckets-zero-no-variance">
              ✓ All non-aligned buckets net to zero and the reconciliation
              variance is within tolerance — nothing to investigate here.
            </p>
          )}
          <GhostVATLinesPanel returnId={returnId} hostVariance={hostVariance} />
          <VATDesyncDrillDown returnId={returnId} hostVariance={hostVariance} />
        </>
      )}
      {nonZero.length > 0 && (
        <>
          <GhostVATLinesPanel returnId={returnId} hostVariance={hostVariance} />
          <VATDesyncDrillDown returnId={returnId} hostVariance={hostVariance} />
        </>
      )}
    </div>
  );
}


// ─── VAT Input Completeness Audit Panel (Feb 2026) ───────────────────
// Direct answer to the CFO/external-auditor question:
//   "Are ALL supplier bills & expense reports posting their VAT_INPUT
//    line to account 11300?"
//
// Fetches /api/reports/vat-input-completeness and displays coverage %
// plus a drill list of documents whose journals lack a VAT_INPUT line.
// Collapsible so it doesn't crowd the main reconciliation view.
// ─── Missing-Group Row (Feb 2026) ──────────────────────────────────
// Expandable row for one journal-group in the VAT audit. Clicking
// the ⚑ chevron reveals the source documents behind the group with
// deep-links to their edit page so the accountant can act
// immediately. Reason-specific guidance in Arabic is surfaced below
// the drilldown.
function MissingGroupRow({ idx, g }) {
  const [open, setOpen] = React.useState(false);
  const [diag, setDiag] = React.useState(null);       // diagnostic modal payload
  const [diagLoading, setDiagLoading] = React.useState(false);
  const delta = Number(g.group_expected || 0) - Number(g.journal_vat_total || 0);
  // Map doc_type → destination route so the accountant can jump
  // straight to the offending source document.
  const docHref = (d) => {
    if (!d) return null;
    if (d.doc_type === 'supplier_bill') {
      return d.doc_id ? `/bill-review/${d.doc_id}` : '/bills-center';
    }
    if (d.doc_type === 'expense' || d.doc_type === 'expense_report') {
      return '/expenses';
    }
    return null;
  };
  const diagnoseDoc = async (docType, docNo) => {
    setDiagLoading(true);
    setDiag({ loading: true, doc_no: docNo });
    try {
      const { data } = await axiosInstance.get(
        `/reports/vat-input-completeness/inspect`,
        { params: { doc_type: docType, doc_no: docNo } });
      setDiag(data);
    } catch (e) {
      setDiag({ error: e?.response?.data?.detail || e.message });
    } finally {
      setDiagLoading(false);
    }
  };
  // Reason-specific Arabic guidance for the accountant
  const guidance = (() => {
    const r = g.reason || '';
    if (r.startsWith('journal_missing_in_gl'))
      return 'المستند يشير إلى قيد لكن هذا القيد غير موجود في GL. الحل: افتح المستند وأعِد ترحيله (re-post) — سيُنشئ المحرك قيداً جديداً مع سطر VAT_INPUT الصحيح.';
    if (r.startsWith('no_journal_no'))
      return 'المستند بدون رقم قيد أصلاً — لم يُرحَّل قط. افتح المستند واعتمده لينشئ قيده.';
    if (r.startsWith('no_vat_input_line'))
      return 'القيد موجود لكن ينقصه سطر VAT_INPUT. الحل: افتح المستند، تحقّق من نسبة الضريبة، ثم أعِد الترحيل.';
    if (r.startsWith('amount_mismatch'))
      return 'القيد موجود لكن مبلغ VAT_INPUT فيه ناقص. أحد المستندات في المجموعة لم تُضَف ضريبته للقيد. افتح كل مستند واعتمد الترحيل من جديد.';
    if (r.startsWith('journal_reversed'))
      return 'القيد المرتبط عُكس (reversed). المستند يحتاج إعادة ترحيل بقيد جديد.';
    return '';
  })();
  return (
    <React.Fragment>
      <tr className="border-t border-rose-100 hover:bg-rose-50 cursor-pointer"
          data-testid={`vat-audit-missing-group-${idx}`}
          onClick={() => setOpen(!open)}>
        <td className="px-2 py-1 text-slate-500">
          {open ? '▼' : '▶'}
        </td>
        <td className="px-2 py-1 font-mono text-[10px]">
          {g.journal_no || '— (no journal)'}
        </td>
        <td className="px-2 py-1 font-mono text-right">{g.doc_count}</td>
        <td className="px-2 py-1 font-mono text-right">{fmt(g.group_expected)}</td>
        <td className="px-2 py-1 font-mono text-right text-emerald-700">{fmt(g.journal_vat_total)}</td>
        <td className="px-2 py-1 font-mono text-right text-rose-700">{fmt(delta)}</td>
        <td className="px-2 py-1 text-[10px] text-rose-700">
          {g.reason?.length > 60 ? g.reason.slice(0, 60) + '…' : g.reason}
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-50" data-testid={`vat-audit-missing-group-drill-${idx}`}>
          <td colSpan={7} className="px-3 py-2">
            {guidance && (
              <p className="text-[11px] text-slate-700 mb-2 italic border-r-2 border-rose-400 pr-2">
                💡 {guidance}
              </p>
            )}
            <table className="w-full text-[11px] border border-slate-200 rounded">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="text-left px-2 py-1">Type</th>
                  <th className="text-left px-2 py-1">Doc #</th>
                  <th className="text-left px-2 py-1">Date</th>
                  <th className="text-left px-2 py-1">Party</th>
                  <th className="text-right px-2 py-1">VAT</th>
                  <th className="text-left px-2 py-1">Action</th>
                  <th className="text-left px-2 py-1">🔬</th>
                </tr>
              </thead>
              <tbody>
                {(g.sample_docs || []).map((d, j) => {
                  const href = docHref(d);
                  return (
                    <tr key={j} className="border-t border-slate-100">
                      <td className="px-2 py-1 text-[10px]">{d.doc_type}</td>
                      <td className="px-2 py-1 font-mono">{d.doc_no || d.doc_id?.slice(0, 8)}</td>
                      <td className="px-2 py-1 font-mono">{d.doc_date}</td>
                      <td className="px-2 py-1">{d.party || '—'}</td>
                      <td className="px-2 py-1 font-mono text-right">{fmt(d.vat)}</td>
                      <td className="px-2 py-1">
                        {href ? (
                          <Link to={href}
                                data-testid={`vat-audit-open-doc-${idx}-${j}`}
                                className="text-indigo-600 hover:underline font-semibold text-[10px]">
                            🔍 افتح المستند
                          </Link>
                        ) : '—'}
                      </td>
                      <td className="px-2 py-1">
                        <button
                          onClick={() => diagnoseDoc(d.doc_type, d.doc_no)}
                          data-testid={`vat-audit-diagnose-${idx}-${j}`}
                          className="text-[10px] rounded border border-slate-300
                                      bg-white hover:bg-amber-50 text-amber-800
                                      px-1.5 py-0.5 font-semibold"
                          title="اكشف حالة القيد الفعلية (raw + attempts)">
                          🔬 تشخيص
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {(g.sample_docs || []).length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-2 py-2 text-[10px] text-slate-500 italic">
                      لا توجد مستندات في هذه المجموعة (قيد يدوي بلا مصدر).
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {diag && (
              <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2"
                   data-testid={`vat-audit-diagnose-panel-${idx}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold text-amber-900">
                    🔬 نتيجة التشخيص — {diag.doc_no || '...'}
                  </span>
                  <button onClick={() => setDiag(null)}
                          className="text-[10px] text-amber-800 hover:underline">
                    إغلاق ✕
                  </button>
                </div>
                {diagLoading && (
                  <div className="text-[10px] text-amber-800">جاري التحميل...</div>
                )}
                {!diagLoading && diag.error && (
                  <div className="text-[10px] text-rose-800">خطأ: {diag.error}</div>
                )}
                {!diagLoading && diag.found === false && (
                  <div className="text-[10px]">{diag.message}</div>
                )}
                {!diagLoading && diag.found === true && (
                  <div className="text-[10px] font-mono space-y-1">
                    <div>
                      journal_id (raw): <b>{diag.journal_id_raw || '—'}</b>
                      {diag.journal_id_type && <span className="text-slate-500"> ({diag.journal_id_type})</span>}
                    </div>
                    <div>journal_no (raw): <b>{diag.journal_no_raw || '—'}</b></div>
                    <div className="text-slate-500 mt-1">
                      Scoping — bills strict: {String(diag.scoping?.documents_strict_mode)},
                      journals strict: {String(diag.scoping?.journals_strict_mode)}
                    </div>
                    <div className="mt-2 space-y-0.5">
                      <div className="font-bold text-amber-900">Attempts:</div>
                      {(diag.attempts || []).map((a, k) => (
                        <div key={k} className={a.found ? 'text-emerald-800' : 'text-rose-800'}>
                          {a.found ? '✓' : '✗'} {a.query}
                          {a.found && a.journal_no && ` → ${a.journal_no}`}
                          {a.cross_tenant && (
                            <span className="text-rose-900 font-bold">
                              {' '}⚠ CROSS-TENANT: journal.company_id={a.journal_company_id}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}


function VATInputCompletenessPanel() {
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [audit, setAudit]     = useState(null);
  const [start, setStart]     = useState('');
  const [end, setEnd]         = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  // Feb 2026 · Root-cause fix — one-click backfill of missing 11300 lines
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);

  const runAudit = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (start) qs.set('start_date', start);
      if (end)   qs.set('end_date',   end);
      if (includeInactive) qs.set('include_inactive', 'true');
      const r = await axiosInstance.get(
        `/reports/vat-input-completeness${qs.toString() ? '?' + qs.toString() : ''}`);
      setAudit(r.data);
      if (!open) setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Audit failed');
    } finally { setLoading(false); }
  };

  const runRepair = async (dryRun = false) => {
    if (!dryRun && !window.confirm(
        'سيتم إضافة/تعديل سطر VAT_INPUT (حساب 11300) تلقائياً لكل قيد ناقص أو زائد، مع تعديل سطر المصروف للحفاظ على التوازن. القيود المفقودة في GL ستُعاد ترحيلها. العملية idempotent — لن تُكرَّر. متابعة؟')) {
      return;
    }
    setRepairing(true);
    try {
      const r = await axiosInstance.post(
        `/reports/vat-input-completeness/repair${dryRun ? '?dry_run=true' : ''}`);
      setRepairResult(r.data);
      const n = r.data?.repaired || 0;
      const recovered = r.data?.total_recovered || 0;
      const blocked = r.data?.blocked || 0;
      const missing = r.data?.missing_journals || {};
      const reposted = missing.reposted || 0;
      const failed = missing.failed || 0;
      const parts = [];
      if (n > 0) parts.push(`أُصلح: ${n} قيد (${recovered.toFixed(2)} ريال)`);
      if (reposted > 0) parts.push(`أُعيد ترحيل: ${reposted} فاتورة (journal_missing_in_gl)`);
      if (blocked > 0) parts.push(`محظور — يحتاج مراجعة يدوية: ${blocked}`);
      if (failed > 0) parts.push(`فشل: ${failed}`);
      if (parts.length === 0) {
        toast.info('لا توجد قيود تحتاج إصلاح — كل شيء مطابق للحسابات ✅');
      } else {
        const level = (blocked > 0 || failed > 0) ? 'warning' : 'success';
        toast[level](parts.join(' · '), { duration: 10000 });
      }
      // Auto-refresh the audit after real repair.
      if (!dryRun) await runAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Repair failed');
    } finally { setRepairing(false); }
  };

  const s        = audit?.summary || {};
  const coverage = Number(s.coverage_pct || 0);
  const barColor = coverage >= 99 ? 'bg-emerald-500'
                   : coverage >= 80 ? 'bg-amber-500'
                   : 'bg-rose-600';
  const chip     = coverage >= 99 ? 'text-emerald-800 bg-emerald-100'
                   : coverage >= 80 ? 'text-amber-800 bg-amber-100'
                   : 'text-rose-800 bg-rose-100';

  return (
    <div className="bg-white border border-slate-200 rounded-lg"
         data-testid="vat-input-completeness-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-indigo-600" />
          <div>
            <div className="text-sm font-bold text-slate-900">
              VAT Input Completeness Audit
              {audit && (
                <span className={`ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${chip}`}
                      data-testid="vat-audit-coverage-chip">
                  {coverage.toFixed(2)}% coverage
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500">
              يفحص كل فاتورة مورد وتقرير مصروفات ويؤكد أن سطر
              VAT_INPUT (حساب 11300) مُسجَّل فعلاً في القيد المحاسبي.
              افتراضياً يستبعد الفواتير الملغاة/المعكوسة/المسودة.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <input type="date" value={start} onChange={e => setStart(e.target.value)}
                  className="border border-slate-300 rounded px-2 py-1 text-xs font-mono"
                  data-testid="vat-audit-start" />
          <span className="text-slate-400">→</span>
          <input type="date" value={end} onChange={e => setEnd(e.target.value)}
                  className="border border-slate-300 rounded px-2 py-1 text-xs font-mono"
                  data-testid="vat-audit-end" />
          <label className="flex items-center gap-1 text-[10px] text-slate-600 cursor-pointer whitespace-nowrap"
                 title="افتراضياً يتم استبعاد الفواتير الملغاة/المعكوسة/المسودة. فعِّل للفحص الجنائي.">
            <input type="checkbox" checked={includeInactive}
                   onChange={e => setIncludeInactive(e.target.checked)}
                   data-testid="vat-audit-include-inactive"
                   className="w-3 h-3" />
            شامل الملغاة/المعكوسة
          </label>
          <Button size="sm" onClick={runAudit} disabled={loading}
                  data-testid="vat-audit-run">
            {loading ? <Loader2 size={12} className="ml-1 animate-spin" /> : <Search size={12} className="ml-1" />}
            فحص الآن
          </Button>
          {audit && (s.missing_doc_count || 0) > 0 && (
            <Button size="sm" onClick={() => runRepair(false)} disabled={repairing}
                    data-testid="vat-audit-repair"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    title="يضيف سطر 11300 VAT_INPUT تلقائياً لكل قيد ناقص · idempotent">
              {repairing ? <Loader2 size={12} className="ml-1 animate-spin" /> : <ShieldCheck size={12} className="ml-1" />}
              إصلاح جذري ({s.missing_doc_count})
            </Button>
          )}
          {audit && (
            <Button size="sm" variant="outline"
                    onClick={() => setOpen(!open)}
                    data-testid="vat-audit-toggle">
              {open ? 'إخفاء' : 'إظهار'}
            </Button>
          )}
        </div>
      </div>
      {audit && open && (
        <div className="p-4 space-y-3" data-testid="vat-audit-body">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-slate-50 rounded p-2 border border-slate-200">
              <div className="text-[10px] uppercase text-slate-500">Documents scanned</div>
              <div className="font-mono font-bold text-lg">{s.documents_scanned || 0}</div>
            </div>
            <div className="bg-slate-50 rounded p-2 border border-slate-200">
              <div className="text-[10px] uppercase text-slate-500">Expected VAT</div>
              <div className="font-mono font-bold text-lg">{fmt(s.expected_vat)}</div>
            </div>
            <div className="bg-slate-50 rounded p-2 border border-slate-200">
              <div className="text-[10px] uppercase text-slate-500">Posted to 11300</div>
              <div className="font-mono font-bold text-lg text-emerald-700">{fmt(s.posted_vat)}</div>
            </div>
            <div className="bg-slate-50 rounded p-2 border border-slate-200">
              <div className="text-[10px] uppercase text-slate-500">Gap (missing)</div>
              <div className="font-mono font-bold text-lg text-rose-700"
                   data-testid="vat-audit-gap">{fmt(s.gap)}</div>
            </div>
            <div className="bg-slate-50 rounded p-2 border border-slate-200">
              <div className="text-[10px] uppercase text-slate-500">Missing docs</div>
              <div className="font-mono font-bold text-lg text-rose-700"
                   data-testid="vat-audit-missing-count">{s.missing_doc_count || 0}</div>
            </div>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full ${barColor}`} style={{ width: `${Math.min(100, coverage)}%` }} />
          </div>
          {s.missing_doc_count > 0 && (
            <div className="rounded border border-rose-200 bg-rose-50 p-3"
                 data-testid="vat-audit-missing-block">
              <div className="text-[11px] font-bold uppercase tracking-wider text-rose-900 mb-2">
                📋 {s.missing_group_count || 0} قيد يُغطّي {s.missing_doc_count} مستنداً وينقصه سطر VAT_INPUT صحيح
              </div>
              {/* Roll-up first — one row per journal (avoids the 42×
                  false-alarm noise from aggregate journals). */}
              {(audit.missing_groups || []).length > 0 && (
                <div className="mb-2 max-h-96 overflow-y-auto rounded border border-rose-100 bg-white"
                     data-testid="vat-audit-missing-groups-table">
                  <table className="w-full text-[11px]">
                    <thead className="bg-rose-100 text-rose-900 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1"></th>
                        <th className="text-left px-2 py-1">Journal</th>
                        <th className="text-right px-2 py-1"># Docs</th>
                        <th className="text-right px-2 py-1">Σ Expected</th>
                        <th className="text-right px-2 py-1">Posted (11300)</th>
                        <th className="text-right px-2 py-1">Δ</th>
                        <th className="text-left px-2 py-1">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(audit.missing_groups || []).map((g, i) => (
                        <MissingGroupRow key={i} idx={i} g={g} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <details className="text-[11px] text-slate-700"
                       data-testid="vat-audit-per-doc-details">
                <summary className="cursor-pointer font-semibold text-rose-800">
                  عرض تفاصيل كل مستند ({s.missing_doc_count})
                </summary>
                <div className="mt-2 max-h-64 overflow-y-auto rounded border border-rose-100 bg-white">
                  <table className="w-full text-[11px]">
                    <thead className="bg-rose-100 text-rose-900 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1">Type</th>
                        <th className="text-left px-2 py-1">No</th>
                        <th className="text-left px-2 py-1">Date</th>
                        <th className="text-left px-2 py-1">Party</th>
                        <th className="text-right px-2 py-1">VAT</th>
                        <th className="text-left px-2 py-1">Journal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(audit.missing || []).slice(0, 200).map((r, i) => (
                        <tr key={i} className="border-t border-rose-100"
                            data-testid={`vat-audit-missing-row-${i}`}>
                          <td className="px-2 py-1 text-[10px]">{r.doc_type}</td>
                          <td className="px-2 py-1 font-mono">{r.doc_no || r.doc_id?.slice(0,8)}</td>
                          <td className="px-2 py-1 font-mono">{r.doc_date}</td>
                          <td className="px-2 py-1">{r.supplier_or_employee}</td>
                          <td className="px-2 py-1 font-mono text-right">{fmt(r.vat_amount)}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">{r.journal_no || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
              <p className="text-[10px] text-rose-800 italic mt-2">
                هذه الفواتير موجودة في الـ Sub-Ledger لكن قيودها المحاسبية لا تحتوي على
                سطر VAT_INPUT في حساب 11300 — أي أن ضريبتها لن تُخصم عند تقديم إقرار
                الـ VAT. اذهب إلى تفاصيل كل فاتورة وأعد فحص Accounting Policy، أو
                استخدم backfill لإضافة السطور الناقصة.
              </p>
            </div>
          )}
          {s.missing_doc_count === 0 && (
            <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-emerald-900 text-[12px]"
                 data-testid="vat-audit-all-clean">
              ✓ جميع الفواتير والمصروفات في هذه الفترة سجّلت VAT_INPUT بشكل صحيح
              في حساب 11300. جاهز للمدقّق الخارجي.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default function ReconciliationCenter() {
  const [period, setPeriod] = useState(defaultPeriod);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);
  const [investigating, setInvestigating] = useState(null);  // check name
  const [vatRootCause, setVatRootCause] = useState(null);
  const [vatRcLoading, setVatRcLoading] = useState(false);
  const [edreIssues, setEdreIssues] = useState([]);
  const [edreScanning, setEdreScanning] = useState(false);

  const loadEDRE = useCallback(async () => {
    try {
      const { data: d } = await axiosInstance.get('/edre/issues');
      setEdreIssues(d.issues || []);
    } catch (e) { console.debug('[edre] load skipped:', e?.message); }
  }, []);

  const runEDREScan = async () => {
    setEdreScanning(true);
    try {
      await axiosInstance.post('/edre/scan');
      await loadEDRE();
      toast.success('اكتمل الفحص التشخيصي الشامل');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الفحص');
    } finally { setEdreScanning(false); }
  };

  const onIssueResolved = (id) => {
    setEdreIssues(is => is.filter(i => i.id !== id));
  };

  const load = useCallback(async () => {
    // Frontend guard — the Backend validates ``YYYY-MM`` and returns 422
    // for partials like ``2026-0``. Since ``period`` is a controlled
    // input, every keystroke would otherwise fire a doomed request.
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) return;
    setLoading(true);
    try {
      const r = await axiosInstance.get(`/reconciliation/full?period=${period}`);
      setData(r.data);
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [period]);

  // Debounce (500 ms) — a defence-in-depth on top of the period-format
  // guard above. Prevents mid-typing requests when the operator edits
  // the period field character-by-character.
  useEffect(() => {
    const t = setTimeout(load, 500);
    return () => clearTimeout(t);
  }, [load]);

  // Load EDRE issues on mount — the center is now issue-aware.
  useEffect(() => { loadEDRE(); }, [loadEDRE]);

  const verdict = data?.verdict || 'UNKNOWN';
  const vm = VERDICT_META[verdict] || VERDICT_META.UNKNOWN;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="reconciliation-center-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
            <ShieldCheck className="text-indigo-600" size={26} /> Financial Truth Reconciliation
          </h1>
          <p className="text-sm text-slate-600">
            Cross-checks 6 financial layers — Revenue, AR, Cash, VAT, Project Profit, Overhead — against the General Ledger.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="recon-period-input"
            type="text"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            placeholder="2026-06"
            className="border border-slate-300 rounded px-3 py-1.5 text-sm font-mono w-28"
          />
          <button
            data-testid="recon-refresh-btn"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
          <Link
            to="/admin/financial-repair"
            data-testid="recon-open-repair-btn"
            className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white rounded px-4 py-1.5 text-sm font-semibold"
            title="Row-level forensic diagnostic + safe backfill"
          >
            <ShieldAlert size={14} /> Repair Tools
          </Link>
        </div>
      </header>

      {data && (
        <>
          <div
            className={`rounded-lg p-5 border-2 ${vm.cls}`}
            data-testid="recon-verdict-banner"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase font-semibold opacity-70">Overall Verdict</div>
                <div className={`text-2xl font-bold mt-1 ${vm.text}`}>{verdict}</div>
                <div className="text-sm mt-1 opacity-80">{vm.label}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase font-semibold opacity-70">Financial Integrity Score</div>
                <div className={`text-5xl font-bold mt-1 ${vm.text}`}>
                  {data.integrity_score}<span className="text-xl opacity-50">%</span>
                </div>
                <div className="text-xs mt-1">
                  <span className="text-emerald-700">{data.summary.pass} pass</span> ·{' '}
                  <span className="text-amber-700">{data.summary.warning} warn</span> ·{' '}
                  <span className="text-rose-700">{data.summary.fail} fail</span>{' '}
                  / {data.summary.total}
                </div>
                {data.overall_confidence !== undefined && (
                  <div className="text-[11px] mt-1 opacity-80" data-testid="overall-confidence">
                    Overall confidence: <span className="font-mono font-bold">{data.overall_confidence}%</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* STA-6: System Assumptions + How It Works */}
          <SystemAssumptionsPanel assumptions={data.system_assumptions} />
          <HowItWorksPanel />

          {/* STA-7 · EDRE — Active diagnostic issues surface */}
          <div className="bg-white border border-slate-200 rounded-lg"
               data-testid="edre-section">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded bg-slate-900 text-white flex items-center justify-center text-[10px] font-bold">EDRE</div>
                <div>
                  <div className="text-sm font-bold text-slate-900">
                    Active Diagnostic Issues
                    {edreIssues.length > 0 && (
                      <span className="ml-2 bg-rose-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        {edreIssues.length}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    كل مشكلة تحمل: ماذا · أين · لماذا · كيف · إصلاح بضغطة · تحقق · منع تكرار
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={runEDREScan} disabled={edreScanning}
                        data-testid="edre-scan-inline">
                  {edreScanning ? <Loader2 size={12} className="ml-1 animate-spin" /> : <RefreshCw size={12} className="ml-1" />}
                  فحص شامل
                </Button>
                <a href="/system-diagnostic" className="text-[11px] text-indigo-700 hover:underline font-semibold"
                   data-testid="open-diagnostic-center">
                  فتح Diagnostic Center →
                </a>
              </div>
            </div>
            {edreIssues.length === 0 ? (
              <div className="p-4 bg-emerald-50 border-t border-emerald-100 flex items-center gap-2">
                <CheckCircle2 size={16} className="text-emerald-600" />
                <div className="text-sm text-emerald-800">
                  لا حالات تشخيصية مفتوحة — النظام نظيف. اضغط &quot;فحص شامل&quot; للتحقق مجدداً.
                </div>
              </div>
            ) : (
              <div className="p-3 space-y-3 bg-slate-50/50">
                {edreIssues.slice(0, 5).map(iss => (
                  <IssueCard key={iss.id} issue={iss} onResolved={onIssueResolved} />
                ))}
                {edreIssues.length > 5 && (
                  <a href="/system-diagnostic"
                     className="block text-center text-[11px] text-indigo-700 hover:underline py-2">
                    عرض {edreIssues.length - 5} حالة إضافية في Diagnostic Center ←
                  </a>
                )}
              </div>
            )}
          </div>

          <VATInputCompletenessPanel />

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="recon-checks-table">
              <thead>
                <tr className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                  <th className="px-3 py-2 w-8"></th>
                  <th className="px-3 py-2">Reconciliation Check</th>
                  <th className="px-3 py-2 text-right">Expected (Sub-Ledger)</th>
                  <th className="px-3 py-2 text-right">Actual (General Ledger)</th>
                  <th className="px-3 py-2 text-right">Variance (SAR)</th>
                  <th className="px-3 py-2 text-center">Status</th>
                  <th className="px-3 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {data.checks.map((c, i) => {
                  const sm = STATUS_META[c.status] || STATUS_META.PASS;
                  const Icon = sm.Icon;
                  const isOpen = openIdx === i;
                  return (
                    <React.Fragment key={i}>
                      <tr
                        className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${isOpen ? 'bg-slate-50' : ''}`}
                        onClick={() => setOpenIdx(isOpen ? null : i)}
                        data-testid={`recon-row-${i}`}
                      >
                        <td className="px-3 py-2.5 text-slate-400">
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="font-medium text-slate-900">{c.name}</div>
                          <div className="flex flex-wrap items-center gap-1 mt-1">
                            <VarianceClassChip vc={c.variance_class} />
                            <ConfidenceBadge score={c.confidence} reasons={c.confidence_reasons} />
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono">{fmt(c.expected)}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{fmt(c.actual)}</td>
                        <td className={`px-3 py-2.5 text-right font-mono font-bold ${
                          c.variance === null || c.variance === undefined ? 'text-slate-400' :
                          Math.abs(c.variance) <= (data.tolerance_sar || 0.5) ? 'text-emerald-700' :
                          c.status === 'FAIL' ? 'text-rose-700' : 'text-amber-700'
                        }`}>
                          {c.variance === null || c.variance === undefined ? '—' :
                            (c.variance > 0 ? '+' : '') + fmt(c.variance)}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white ${sm.badge}`}>
                            <Icon size={10} /> {c.status}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                          {c.status === 'ACTION_NEEDED' && c.action?.link && (
                            <a
                              data-testid={`action-btn-${i}`}
                              href={c.action.link}
                              className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-semibold rounded px-2 py-1"
                              title={c.action.label || 'Take action'}
                            >
                              {c.action.label || 'Take Action'}
                            </a>
                          )}
                          {CHECK_NAME_TO_KEY[c.name] && c.status !== 'PASS'
                            && c.status !== 'MATCH'
                            && c.status !== 'NOT_DUE_YET'
                            && c.status !== 'NOT_CONFIGURED'
                            && c.status !== 'ACTION_NEEDED' && (
                            <button
                              data-testid={`investigate-btn-${i}`}
                              onClick={() => setInvestigating(c.name)}
                              className="inline-flex items-center gap-1 bg-sky-600 hover:bg-sky-700 text-white text-[10px] font-semibold rounded px-2 py-1"
                              title="Open forensic investigation"
                            >
                              <Search size={10} /> Investigate
                            </button>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className={`border-t border-slate-100 ${sm.cls.split(' ').filter(s => s.startsWith('bg-')).join(' ')} bg-opacity-30`}>
                          <td colSpan={7} className="px-6 py-3 text-xs">
                            {c.details?.filing_envelope && (
                              <div
                                data-testid={`vat-envelope-banner-${i}`}
                                className="mb-3 flex items-start gap-3 rounded-lg border border-sky-300 bg-sky-50/70 px-3 py-2"
                              >
                                <Info size={14} className="mt-0.5 shrink-0 text-sky-700" />
                                <div className="flex-1 leading-relaxed">
                                  <div className="text-[11px] font-semibold uppercase tracking-wider text-sky-800">
                                    VAT Filing Envelope
                                  </div>
                                  <div className="mt-0.5 text-sm text-slate-800">
                                    This month belongs to <span className="font-bold text-sky-900">{c.details.filing_envelope.label_en}</span>{' '}
                                    <span className="text-slate-500">
                                      ({c.details.filing_envelope.months?.join(' · ')})
                                    </span>
                                  </div>
                                  <div className="mt-0.5 text-[11px] text-slate-600">
                                    Envelope window: {c.details.filing_envelope.start_date} → {c.details.filing_envelope.end_date}
                                    {' · '}Filing deadline: <span className="font-mono">{c.details.filing_envelope.deadline_date}</span>
                                    {' · '}Cadence: <span className="font-mono">{c.details.filing_envelope.filing_frequency}</span>
                                  </div>
                                </div>
                              </div>
                            )}
                            {c.notes && (
                              <div className="mb-2">
                                <span className="font-bold opacity-70">Notes: </span>
                                <span className="opacity-90">{c.notes}</span>
                              </div>
                            )}
                            {(c.confidence_reasons && c.confidence_reasons.length > 0) && (
                              <div className="mb-2 bg-white/60 border border-white/60 rounded p-2"
                                   data-testid={`confidence-reasons-${i}`}>
                                <div className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-1">
                                  لماذا هذه الموثوقية؟ (Confidence: {c.confidence}%)
                                </div>
                                <ul className="text-[11px] space-y-0.5">
                                  {c.confidence_reasons.map((r, ri) => (
                                    <li key={ri} className="font-mono">• {r}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {c.details && (
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
                                {Object.entries(c.details).filter(([k]) => k !== 'drill_down' && k !== 'filing_envelope').map(([k, v]) => (
                                  <div key={k} className="bg-white/50 rounded p-2 border border-white/60">
                                    <div className="text-[10px] uppercase opacity-60 tracking-wider">
                                      {k.replace(/_/g, ' ')}
                                    </div>
                                    <div className="font-mono text-sm font-bold mt-0.5">
                                      {typeof v === 'number' ? fmt(v) : String(v)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {c.details?.drill_down && (
                              <VATDrillDown drill={c.details.drill_down}
                                             returnId={c.details.return_id}
                                             hostVariance={c.variance}
                                             hostDetails={c.details} />
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-sm font-bold text-slate-900 mb-2">GL Context</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { lbl: 'GL Revenue (41xxx)',     val: data.context.gl_revenue,        cls: 'text-emerald-700' },
                { lbl: 'GL Direct Cost (51xxx)', val: data.context.gl_direct_51,      cls: 'text-amber-700' },
                { lbl: 'GL Unallocated (52xxx)', val: data.context.gl_unallocated_52, cls: 'text-orange-700' },
                { lbl: 'GL Allocated OH (53xxx)',val: data.context.gl_allocated_53,   cls: 'text-rose-700' },
              ].map((c, i) => (
                <div key={i} className="bg-slate-50 rounded p-2.5 border border-slate-200">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{c.lbl}</div>
                  <div className={`text-base font-bold font-mono mt-0.5 ${c.cls}`}>{fmt(c.val)}</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-3">
              Tolerance: ±{data.tolerance_sar} SAR (rounding-level). Engine is read-only — it surfaces, never fixes.
            </p>
          </div>
        </>
      )}
      {!data && !loading && (
        <div className="text-center text-slate-500 text-sm py-12 border border-dashed border-slate-300 rounded-lg">
          Pick a period and click <strong>Refresh</strong>.
        </div>
      )}

      <InvestigationDrawer
        open={!!investigating}
        onClose={() => setInvestigating(null)}
        period={period}
        checkName={investigating}
      />
    </div>
  );
}
